import type { SearchKeyValueParam } from '../../types';
import {
  FUNNELBACK_LOOKUP_QUERY_PARAM,
  SEARCH_URL_FACET_KEY_PREFIX,
} from '../../context/search-app/search-url';

/** Funnelback Date drill-down query key (`f.Date|d`). */
export const FUNNELBACK_DATE_FACET_PARAM_KEY = 'f.Date|d';

/**
 * Encode a Date facet value for Funnelback query strings.
 * Keeps `=` literal (e.g. `d=2026 :: 2026` → `d=2026+%3A%3A+2026`).
 */
export function encodeFunnelbackDateFacetParamValue(value: string): string {
  return value
    .replace(/ /g, '+')
    .replace(/:/g, '%3A')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E');
}

function encodeFunnelbackFacetParamKey(key: string): string {
  return key.replace(/\|/g, '%7C');
}

/** Standard facet value encoding (commas, spaces, etc.). */
function encodeStandardFacetParamValue(value: string): string {
  const params = new URLSearchParams();
  params.append('_', value);
  const encoded = params.toString().slice(2);
  return encoded;
}

/** Serialize one `f.*` query pair using Funnelback-expected encoding. */
export function formatFunnelbackFacetQueryPair(
  key: string,
  value: string,
): string {
  const encodedKey = encodeFunnelbackFacetParamKey(key);
  const encodedValue =
    key === FUNNELBACK_DATE_FACET_PARAM_KEY
      ? encodeFunnelbackDateFacetParamValue(value)
      : encodeStandardFacetParamValue(value);
  return `${encodedKey}=${encodedValue}`;
}

function applyExtras(
  params: URLSearchParams,
  extraParams?: SearchKeyValueParam[],
): string[] {
  const facetSegments: string[] = [];
  for (const kv of extraParams ?? []) {
    if (kv.key.startsWith(SEARCH_URL_FACET_KEY_PREFIX)) {
      facetSegments.push(formatFunnelbackFacetQueryPair(kv.key, kv.value));
    } else {
      params.set(kv.key, kv.value);
    }
  }
  return facetSegments;
}

function joinQueryParts(base: string, facetSegments: string[]): string {
  if (facetSegments.length === 0) {
    return base;
  }
  return base ? `${base}&${facetSegments.join('&')}` : facetSegments.join('&');
}

/**
 * Builds a search URL from a lookup base (path or absolute URL), the search term, and extra query pairs.
 * Uses Funnelback JSON++ `query` (not `q`) and applies extras so non-facet keys last-write-wins, `f.*` facets append.
 */
export function buildLookupSearchUrl(
  lookupUrl: string,
  query: string,
  extraParams?: SearchKeyValueParam[],
): string {
  const trimmedQuery = query.trim();
  const trimmed = lookupUrl.trim();
  if (!trimmed) {
    const params = new URLSearchParams();
    params.set(FUNNELBACK_LOOKUP_QUERY_PARAM, trimmedQuery);
    const facetSegments = applyExtras(params, extraParams);
    return `?${joinQueryParts(params.toString(), facetSegments)}`;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    const u = new URL(trimmed);
    const merged = new URLSearchParams(u.search);
    merged.delete('q');
    merged.delete(FUNNELBACK_LOOKUP_QUERY_PARAM);
    merged.set(FUNNELBACK_LOOKUP_QUERY_PARAM, trimmedQuery);
    const facetSegments = applyExtras(merged, extraParams);
    const queryString = joinQueryParts(merged.toString(), facetSegments);
    return `${u.origin}${u.pathname}${queryString ? `?${queryString}` : ''}`;
  }
  const params = new URLSearchParams();
  params.set(FUNNELBACK_LOOKUP_QUERY_PARAM, trimmedQuery);
  const facetSegments = applyExtras(params, extraParams);
  const queryString = joinQueryParts(params.toString(), facetSegments);
  const join = trimmed.includes('?') ? '&' : '?';
  return `${trimmed}${join}${queryString}`;
}
